import { Component, Inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import startCase from 'lodash/startCase';

import { CoreApiService } from '../../core-api.service';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { ProjectsService } from '../../projects/projects.service';
import { SessionService } from '../session.service';
import { formatDate } from '../../shared/date.utils';
import { PermissionAction } from '../../shared/enums/permission-action.enum';
import { BulkMoveSessions, BulkMoveSessionsConflict, BulkMoveSessionsConflictMode as ConflictMode } from '../../shared/models/bulk.model';
import { Session } from '../../shared/models/session.model';

interface MoveSessionsToProjectDialogData {
  sessions: Session[]
}

interface ConflictSession {
  isSessionConflict: boolean
  sessionId: string
  sessionLabel: string
  subjectCode: string
  timestamp: string // formatted
}

interface ConflictForm {
  [sessionId: string]: string
  projectId: string
  resolveAll: string
}

@Component({
  host: {
    'test-id': 'move-sessions-to-project-dialog',
  },
  selector: 'move-sessions-to-project-dialog',
  templateUrl: './move-sessions-to-project-dialog.component.html',
  styleUrls: ['./move-sessions-to-project-dialog.component.scss'],
})
export class MoveSessionsToProjectDialog {
  // exclude 'dry' from dropdown menu
  readonly conflictModeOptions: string[] = Object.keys(ConflictMode)
    .filter(v => ConflictMode[v] !== ConflictMode.DRY)
    .map(v => ConflictMode[v])
  conflicts: ConflictSession[] = []
  readonly form = new FormGroup({
    projectId: new FormControl(null, [Validators.required]),
    resolveAll: new FormControl(null),
  } as { [key in keyof ConflictForm]: FormControl<ConflictForm[key]> })
  isPreDryRun = true
  movableSessionIds: string[] = []
  readonly groups$ = this.projectsService.getGroupedProjects([PermissionAction.ContainersCreateHierarchy])
  readonly startCase = startCase

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: MoveSessionsToProjectDialogData,
    private dialogRef: MatDialogRef<MoveSessionsToProjectDialog, boolean>,
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private notifications: NotificationService,
    private projectsService: ProjectsService,
    private sessionService: SessionService,
  ) {
    this.setMovableSessionIds();
  }

  get hasConflicts(): boolean {
    return this.conflicts.length > 0;
  }

  getGroupLabel(group): string {
    return `${group.label} (${group._id})`;
  }

  private setMovableSessionIds(): void {
    const formValues = this.form.value;
    let sessionIds = this.data.sessions.map(session => session._id);
    if (this.hasConflicts) {
      sessionIds = sessionIds.filter(id => formValues[id] !== ConflictMode.SKIP);
    }
    this.movableSessionIds = sessionIds;
  }

  private setConflictingSessions(conflicts: BulkMoveSessionsConflict[]): void {
    const sessions = new Map(this.data.sessions.map(session => [session._id, session]));
    this.conflicts = conflicts.map(conflict => {
      const session = sessions.get(conflict.id);
      return {
        isSessionConflict: Boolean(conflict.label),
        sessionId: conflict.id,
        sessionLabel: session?.label || conflict.label,
        subjectCode: conflict.subject_code,
        timestamp: isNaN(Date.parse(session?.timestamp)) ? '' : formatDate(new Date(session.timestamp), 'yyyy-MM-dd HH:mm'),
      };
    });
  }

  private refreshResolutionFormControls(): void {
    // remove any possible conflict fields from form group (they are named by session._id)
    this.data.sessions.forEach(session => {
      this.form.removeControl(session._id);
    });
    // now add only the conflicting sessions
    this.conflicts.forEach(conflict => {
      this.form.addControl(
        conflict.sessionId,
        new FormControl(ConflictMode.SKIP),
      );
    });
    // reset resolveAll value
    this.form.controls.resolveAll.setValue(ConflictMode.SKIP);
  }

  onConflictChange(): void {
    this.setMovableSessionIds();
    this.form.controls.resolveAll.setValue(null);
  }

  onResolveAllChange(): void {
    this.conflicts.forEach(conflict => {
      this.form.controls[conflict.sessionId].setValue(
        this.form.controls.resolveAll.value
      );
    });
    this.setMovableSessionIds();
  }

  onProjectChange(): void {
    this.isPreDryRun = true;
    this.refreshResolutionFormControls();
    this.setMovableSessionIds();
    this.submit(ConflictMode.DRY);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSubmit(): void {
    this.submit(ConflictMode.MOVE);
  }

  private submit(conflictMode: ConflictMode): void {
    if (this.form.invalid) {
      return;
    }
    this.form.disable();
    const payload: BulkMoveSessions = {
      destination_container_type: 'projects',
      sources: this.movableSessionIds,
      destinations: [this.form.get('projectId').value],
      conflict_mode: conflictMode,
    };
    if (payload.sources.length === 0) {
      this.notifications.success('session.moveSessionsToProject.noop');
      this.dialogRef.close();
      return;
    }
    this.coreApi.bulk.move.sessions(payload).subscribe({
      next: conflicts => {
        this.form.enable();
        this.isPreDryRun = false;
        this.setConflictingSessions(conflicts);
        if (this.hasConflicts) {
          this.refreshResolutionFormControls();
          this.setMovableSessionIds();
          return;
        }
        if (payload.conflict_mode === ConflictMode.MOVE) {
          this.notifications.success('session.moveSessionsToProject.success');
          this.sessionService.clearCheckedSessions();
          // indicate whether or not to reload state of parent component
          this.dialogRef.close(true);
        }
      },
      error: error => {
        this.logger.error(error);
        this.notifications.error('session.moveSessionsToProject.error');
        this.form.enable();
      },
    });
  }
}
